import { Cause, Context, Effect, Layer, Queue, Stream } from 'effect'

/**
 * Browser upload boundary for presigned `PUT` requests.
 *
 * Semantics:
 * - success resolves on any 2xx `load`
 * - non-2xx `load` fails with `HttpError`
 * - `error` fails with `NetworkError`
 * - interruption remains Effect interruption and aborts the underlying `XMLHttpRequest`
 * - synchronous setup failures (`open`, `setRequestHeader`, `send`) are defects
 */
export type UploadProgress = {
  uploadedBytes: number
  totalBytes: number
}

export type XhrUploadError =
  | { _tag: 'NetworkError' }
  | { _tag: 'HttpError'; status: number; statusText: string }

type XhrUploadService = {
  putPresignedUrl: (args: {
    url: string
    headers: Record<string, unknown>
    file: File
  }) => Stream.Stream<UploadProgress, XhrUploadError>
}

export class XhrUpload extends Context.Service<XhrUpload, XhrUploadService>()('XhrUpload') {}

export function xhrUploadLayer() {
  return Layer.succeed(XhrUpload, {
    putPresignedUrl: ({ url, headers, file }) =>
      Stream.callback<UploadProgress, XhrUploadError>((queue) =>
        Effect.acquireRelease(
          Effect.sync(() => {
            const xhr = new XMLHttpRequest()
            let settled = false

            // Browser events can race. Only the first terminal event closes or
            // fails the Stream; later load/error callbacks are ignored.
            const finish = (complete: () => void) => {
              if (settled) {
                return
              }

              settled = true
              complete()
            }
            const fail = (error: XhrUploadError) => Queue.failCauseUnsafe(queue, Cause.fail(error))

            xhr.upload.onprogress = (event) => {
              Queue.offerUnsafe(queue, {
                uploadedBytes: event.loaded,
                totalBytes: file.size
              })
            }

            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                finish(() => Queue.endUnsafe(queue))
                return
              }

              finish(() =>
                fail({
                  _tag: 'HttpError',
                  status: xhr.status,
                  statusText: xhr.statusText
                })
              )
            }

            xhr.onerror = () => {
              finish(() => fail({ _tag: 'NetworkError' }))
            }

            xhr.open('PUT', url, true)
            for (const [key, value] of Object.entries(headers)) {
              xhr.setRequestHeader(key, String(value))
            }
            xhr.send(file)

            return () => {
              // Detach first so browser callbacks cannot race the Stream scope
              // that interruption is closing.
              xhr.upload.onprogress = null
              xhr.onload = null
              xhr.onerror = null

              if (settled) {
                return
              }

              settled = true
              xhr.abort()
            }
          }),
          (cleanup) => Effect.sync(cleanup)
        ).pipe(
          Effect.catchCause((cause) =>
            Effect.sync(() => {
              Queue.failCauseUnsafe(queue, cause)
            })
          )
        )
      )
  } satisfies XhrUploadService)
}
